import React, { useState, useEffect } from 'react';
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  Zap,
  Users,
  MessageSquare,
  ArrowRight,
  Globe,
  Shield,
  Brain
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';


const ModernNavbar = () => {

  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();


  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Set background blur state
      setIsScrolled(currentScrollY > 10);

      // Hide/show navbar based on scroll direction
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        // Scrolling up or near top - show navbar
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past threshold - hide navbar
        setIsVisible(false);
        setActiveDropdown(''); // Close any open dropdowns
        setIsMobileMenuOpen(false); // Close mobile menu
      }

      setLastScrollY(currentScrollY);
    };

    const handleMouseMove = (e: any) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const navItems = [
    { name: 'Home', href: '/home' },
    {
      name: 'Platform',
      href: '#platform',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Agent Studio', href: '/agentstudio', icon: <Brain className="w-4 h-4" />, desc: 'Visual agent builder' },
        { name: 'Selfic Workflows', href: '/selficworkflows', icon: <Zap className="w-4 h-4" />, desc: 'Automation engine' },
        { name: 'Selfic Assistant/Search', href: '/selficassistant', icon: <Globe className="w-4 h-4" />, desc: 'Data sources' },
      ]
    },
    { name: 'Solutions', href: '/solutions' },
    { name: 'Security', href: '/security' },
    { name: 'About Us', href: '/aboutus' },
    { name: 'Contact Us', href: '/contactus' }
  ];

  return (
    <>
      {/* Backdrop Blur Overlay */}
      <div className={`fixed inset-0 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
      </div>

      <nav className={`fixed w-full z-50 transition-all duration-300 ${isVisible ? 'translate-y-0' : '-translate-y-full'
        } ${isScrolled
          ? 'bg-black/95 backdrop-blur-xl border-b border-white/10 shadow-2xl shadow-purple-500/10'
          : 'bg-transparent border-b border-transparent'
        }`}>
        {/* Animated Background Gradient */}
        <div
          className="absolute inset-0 opacity-20 transition-opacity duration-1000"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.15), transparent 40%)`
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex justify-between items-center w-full">
            {/* Logo Section */}
            <div className="flex items-center space-x-3 group cursor-pointer flex-shrink-0">
              <div className="relative">


                {/* Glowing Ring Effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 via-purple-500 to-cyan-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 animate-pulse" />
              </div>

            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-2 flex-1 justify-center max-w-2xl">
              {navItems.map((item, index) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown('')}
                >
                  <a
                    href={item.href}
                    className="flex items-center space-x-1 px-3 py-2 rounded-xl text-gray-300 hover:text-white transition-all duration-300 hover:bg-white/10 group whitespace-nowrap"
                  >
                    <span className="font-medium">{item.name}</span>
                    {item.hasDropdown && (
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''
                        }`} />
                    )}
                  </a>

                  {/* Dropdown Menu */}
                  {item.hasDropdown && activeDropdown === item.name && (
                    <div className="absolute top-full left-0 mt-2 w-80 bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-purple-500/20 p-2">
                      <div className="grid gap-1">
                        {item.dropdownItems.map((dropdownItem, i) => (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/10 transition-colors group"
                          >
                            <div className="text-purple-400 group-hover:text-blue-400 transition-colors">
                              {dropdownItem.icon}
                            </div>
                            <div>
                              <div className="font-medium text-white text-sm">{dropdownItem.name}</div>
                              <div className="text-xs text-gray-400">{dropdownItem.desc}</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-purple-400 ml-auto opacity-0 group-hover:opacity-100 transition-all" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => { navigate('/login'); }} className="z-50 bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2 text-white cursor-pointer">
                <span>Get Demo</span>
                <ArrowRight className="w-4 h-4" />
              </button>

            </div>


            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`lg:hidden overflow-hidden transition-all duration-500 ${isMobileMenuOpen ? 'max-h-screen opacity-100 mt-6' : 'max-h-0 opacity-0'
            }`}>
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/20 p-4 space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="flex items-center justify-between p-3 rounded-xl hover:bg-white/10 transition-colors group"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="text-white font-medium">{item.name}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-purple-400 transition-colors" />
                </a>
              ))}

              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <a
                  href="/login"
                  className="block p-3 text-center text-white hover:bg-white/10 rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </a>
                <button
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate('/login'); // replace '/login' with your actual route if different
                  }}
                >
                  Start Building
                </button>

              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default ModernNavbar;